import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Home, Ruler, Calendar, ExternalLink, Loader2, Tag } from 'lucide-react';
import PropertyMap from './PropertyMap';
import api from '../utils/axiosConfig';
import { formatPrice, formatArea } from '../utils/format';

const PropertyDetail = ({ property, onBack, userQuery, currencyPreference = 'USD', areaUnitPreference = 'sqm', fxRate = null }) => {
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!property) return;
    
    const abortController = new AbortController();
    
    const fetchAISummary = async () => {
      setLoadingSummary(true);
      try {
        // Find property index in the list
        const response = await api.get('/api/properties', { signal: abortController.signal });
        const allProperties = response.data;
        const propertyIndex = allProperties.findIndex(p => 
          p.title === property.title && 
          p.address === property.address &&
          p.price === property.price
        );

        if (propertyIndex >= 0) {
          const summaryResponse = await api.post(`/api/properties/${propertyIndex}/summary`, {
            userQuery: userQuery || null
          }, { signal: abortController.signal });
          if (summaryResponse.data.success) {
            setAiSummary(summaryResponse.data.summary);
          }
        }
      } catch (error) {
        // Ignore AbortError - request was cancelled intentionally
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          return;
        }
        console.error('Error fetching AI summary:', error);
        // Fallback to original description
        setAiSummary(null);
      } finally {
        if (!abortController.signal.aborted) {
          setLoadingSummary(false);
        }
      }
    };
    
    fetchAISummary();
    
    return () => {
      abortController.abort();
    };
  }, [property, userQuery]);

  const imageUrl = (property.image_urls && property.image_urls.length > 0)
    ? property.image_urls[0]
    : (property.imageUrl && typeof property.imageUrl === 'string' && property.imageUrl.trim())
      ? property.imageUrl.trim()
      : 'https://via.placeholder.com/800x600?text=No+Image';

  const listingUrl = property.url || property.homeUrl;
  const sourceRaw = (property.source || property.listingSource || '').toString();
  const sourceLabel = (() => {
    const u = (listingUrl || '').toString().toLowerCase();
    const s = sourceRaw.toLowerCase();
    if (s.includes('trulia') || u.includes('trulia.com')) return 'Trulia';
    if (s.includes('pararius') || u.includes('pararius')) return 'Pararius';
    if (s.includes('zillow') || u.includes('zillow.com')) return 'Zillow';
    return sourceRaw ? sourceRaw.charAt(0).toUpperCase() + sourceRaw.slice(1) : 'Listing';
  })();

  const bedsDisplay = (() => {
    if (property.beds === 0) return 'Studio';
    if (property.beds != null) return String(property.beds);
    if (property.rooms != null) return String(property.rooms);
    if (property.bedrooms) return String(property.bedrooms);
    return null;
  })();

  const bathsDisplay = (() => {
    if (property.baths != null) return String(property.baths);
    if (property.bathrooms) return String(property.bathrooms);
    return null;
  })();

  const areaDisplay = formatArea(property, areaUnitPreference);
  const displayPrice = formatPrice(property, currencyPreference, fxRate);

  const listedDisplay = (() => {
    const raw = (property.dateListed || '').toString().trim();
    if (!raw) return null;
    const dt = new Date(raw);
    if (!Number.isFinite(dt.getTime())) return raw;

    // Show a clean US date (optionally time if it was today)
    const now = new Date();
    const isSameDay =
      dt.getFullYear() === now.getFullYear() &&
      dt.getMonth() === now.getMonth() &&
      dt.getDate() === now.getDate();

    const datePart = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(dt);

    if (!isSameDay) return datePart;

    const timePart = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    }).format(dt);

    return `${datePart} • ${timePart}`;
  })();

  const typeDisplay = (() => {
    const raw = (property.propertyType || '').toString().trim();
    if (!raw) return null;
    const normalized = raw.replace(/_/g, ' ').toLowerCase();

    // Hide overly-generic/noisy types
    if (normalized === 'home rentalcommunity' || normalized === 'home' || normalized.startsWith('home ')) {
      // Keep only if it actually conveys something useful
      if (normalized.includes('rentalcommunity')) return 'Rental community';
      return null;
    }

    // Nicify common patterns
    const titleCase = normalized.replace(/\b\w/g, (c) => c.toUpperCase());
    return titleCase;
  })();

  const tagsDisplay = (() => {
    const t = (property.tags || '').toString().trim();
    if (!t) return null;
    return t;
  })();

  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Back Button */}
        <motion.button
          type="button"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -3 }}
          whileTap={{ scale: 0.95 }}
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:image.png"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm sm:text-base">Back to results</span>
        </motion.button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {property.image_urls && property.image_urls.length > 0 ? (
              <div className="space-y-4">
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
                  <img
                    src={property.image_urls[0]}
                    alt={property.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x600?text=No+Image';
                    }}
                  />
                </div>
                {property.image_urls.length > 1 && (
                  <div className="grid grid-cols-3 gap-2">
                    {property.image_urls.slice(1, 4).map((url, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 * (index + 1) }}
                        className="aspect-square rounded-lg overflow-hidden bg-gray-100"
                      >
                        <img
                          src={url}
                          alt={`${property.title} - ${index + 2}`}
                          className="w-full h-full object-cover"
                        />
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
                <img
                  src={imageUrl}
                  alt={property.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <PropertyMap property={property} className="w-full" />
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            {/* Header */}
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                {property.title}
              </h1>
              <div className="flex items-center gap-2 text-gray-600 mb-4">
                <MapPin className="w-4 h-4" />
                <span className="text-sm sm:text-base">{property.address}, {property.city}</span>
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                {displayPrice}
                <span className="text-lg text-gray-600 font-normal">/month</span>
              </div>
            </div>

            {/* Key Facts */}
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Key facts</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {areaDisplay && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white p-4 rounded-lg border border-gray-200"
                >
                  <Ruler className="w-5 h-5 text-gray-700 mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Size</p>
                  <p className="text-lg font-semibold text-gray-900">{areaDisplay}</p>
                </motion.div>
              )}
              {bedsDisplay && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="bg-white p-4 rounded-lg border border-gray-200"
                >
                  <Home className="w-5 h-5 text-gray-700 mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Beds</p>
                  <p className="text-lg font-semibold text-gray-900">{bedsDisplay}</p>
                </motion.div>
              )}
              {bathsDisplay && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.55 }}
                  className="bg-white p-4 rounded-lg border border-gray-200"
                >
                  <p className="text-xs text-gray-500 mb-1">Baths</p>
                  <p className="text-lg font-semibold text-gray-900">{bathsDisplay}</p>
                </motion.div>
              )}
              {property.furnished && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                  className="bg-white p-4 rounded-lg border border-gray-200"
                >
                  <p className="text-xs text-gray-500 mb-1">Furnished</p>
                  <p className="text-lg font-semibold text-gray-900">{property.furnished}</p>
                </motion.div>
              )}
              {typeDisplay && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 }}
                  className="bg-white p-4 rounded-lg border border-gray-200"
                >
                  <p className="text-xs text-gray-500 mb-1">Type</p>
                  <p className="text-lg font-semibold text-gray-900">{typeDisplay}</p>
                </motion.div>
              )}
              {listedDisplay && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 }}
                  className="bg-white p-4 rounded-lg border border-gray-200"
                >
                  <Calendar className="w-5 h-5 text-gray-700 mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Listed</p>
                  <p className="text-lg font-semibold text-gray-900">{listedDisplay}</p>
                </motion.div>
              )}
              {tagsDisplay && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.85 }}
                  className="bg-white p-4 rounded-lg border border-gray-200 sm:col-span-2 lg:col-span-3"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-5 h-5 text-gray-700" />
                    <p className="text-xs text-gray-500">Tags</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{tagsDisplay}</p>
                </motion.div>
              )}
            </div>
            </div>

            {/* Description */}
            {(property.description || aiSummary) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200"
              >
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-gray-900">Description</h2>
                  {aiSummary && property.description && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowOriginal(!showOriginal);
                      }}
                      className="text-xs text-gray-600 hover:text-gray-900 font-medium"
                    >
                      {showOriginal ? 'Show AI summary' : 'Show original'}
                    </button>
                  )}
                </div>
                {loadingSummary ? (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Generating summary...</span>
                  </div>
                ) : (
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    {showOriginal && property.description 
                      ? property.description 
                      : (aiSummary || property.description)}
                  </p>
                )}
              </motion.div>
            )}

            {/* Details */}
            {property.details && Object.keys(property.details).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200"
              >
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
                <div className="space-y-2">
                  {Object.entries(property.details).map(([key, value], index) => (
                    <motion.div
                      key={key}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.1 + index * 0.05 }}
                      className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0"
                    >
                      <span className="text-sm text-gray-600 capitalize">{key}:</span>
                      <span className="text-sm font-medium text-gray-900">{value}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Route Info */}
            {property.routeInfo && property.routeInfo.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="bg-blue-50 rounded-lg p-4 sm:p-6 border border-blue-200"
              >
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Distance</h2>
                {property.routeInfo.map((route, index) => (
                  <div key={index} className="text-sm text-gray-700">
                    <p className="font-medium">{route.requirement || 'Distance'}</p>
                    <p className="text-gray-600">
                      {route.walkingMinutes} min walk • {route.distanceKm} km
                    </p>
                  </div>
                ))}
              </motion.div>
            )}

            {/* Action Button */}
            {listingUrl && (
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href={listingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gray-900 text-white py-3 sm:py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
              >
                <span>{`View on ${sourceLabel}`}</span>
                <ExternalLink className="w-4 h-4" />
              </motion.a>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
